import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { drafts as idb, type Draft } from './storage';

/**
 * Drafts live in Redux for the editing session and in IndexedDB for durability.
 * The slice never blocks on IndexedDB — a storage failure must not stop someone
 * writing a letter, so persistence errors are recorded rather than thrown.
 */
interface DraftsState {
  byId: Record<string, Draft>;
  saving: boolean;
  lastError: string | null;
}

const initialState: DraftsState = { byId: {}, saving: false, lastError: null };

export const persistDraft = createAsyncThunk(
  'drafts/persist',
  async (draft: Draft) => {
    await idb.put(draft);
    return draft.id;
  },
);

export const forgetDraft = createAsyncThunk('drafts/forget', async (id: string) => {
  await idb.remove(id);
  return id;
});

/** Sign-out: drop every trace of unsent writing. */
export const wipeDrafts = createAsyncThunk('drafts/wipe', async () => {
  await idb.clear();
});

export const loadDrafts = createAsyncThunk('drafts/load', () => idb.all());

const draftsSlice = createSlice({
  name: 'drafts',
  initialState,
  reducers: {
    /** Called on every keystroke batch; the durable write goes through persistDraft. */
    updated(state, action: PayloadAction<Draft>) {
      state.byId[action.payload.id] = action.payload;
      state.saving = true;
      state.lastError = null;
    },
  },
  extraReducers: (b) => {
    // A load can finish after someone has started typing (several screens ask
    // for one), so merge: what is on screen wins unless storage is newer.
    b.addCase(loadDrafts.fulfilled, (state, action) => {
      for (const d of action.payload) {
        const current = state.byId[d.id];
        if (!current || current.updatedAt < d.updatedAt) state.byId[d.id] = d;
      }
    });
    b.addCase(persistDraft.pending, (state) => {
      state.saving = true;
      state.lastError = null;
    });
    b.addCase(persistDraft.fulfilled, (state) => {
      state.saving = false;
    });
    b.addCase(persistDraft.rejected, (state, action) => {
      state.saving = false;
      state.lastError = action.error.message ?? 'draft could not be saved';
    });
    b.addCase(forgetDraft.fulfilled, (state, action) => {
      delete state.byId[action.payload];
    });
    b.addCase(wipeDrafts.fulfilled, (state) => {
      state.byId = {};
      state.lastError = null;
    });
  },
});

export const { updated } = draftsSlice.actions;
export default draftsSlice.reducer;
